import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { configService } from '@/shared/lib/configService';

/**
 * Token refresh request for OAuth endpoint
 * Refresh token is sent automatically via HttpOnly cookie
 * No need to include it in the request body
 */
export interface TokenRefreshRequest {
  grant_type: 'refresh_token';
}

/**
 * Token refresh response from OAuth endpoint
 * Note: Tokens are NOT in the response body
 * They're set by the server via Set-Cookie headers
 */
export interface TokenRefreshResponse {
  data: {
    access_token_expires_in: number;
    refresh_token_expires_in: number;
  };
}

/**
 * Token API for OAuth token refresh
 * Uses separate baseQuery with credentials: 'include' for HttpOnly cookies
 *
 * Endpoint: POST /api/oauth/token
 * Request: { grant_type: 'refresh_token' }
 * Response Headers: Set-Cookie: accessToken=...; Set-Cookie: refreshToken=...
 * Response Body: { data: { access_token_expires_in, refresh_token_expires_in } }
 */
export const tokenApi = createApi({
  reducerPath: 'tokenApi',
  baseQuery: fetchBaseQuery({
    baseUrl: configService.getApiHost(),
    // Critical: Include credentials to send/receive HttpOnly cookies
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
      const lang = localStorage.getItem('i18nextLng') || 'en';
      headers.set('Accept-Language', lang);
      // Disable caching for token refresh
      headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
      headers.set('Pragma', 'no-cache');
      headers.set('Expires', '0');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    /**
     * Refresh access token using refresh_token
     * Endpoint: POST /api/oauth/token
     * Used when access token expires
     *
     * How it works:
     * 1. Browser automatically sends refreshToken cookie
     * 2. Server validates refreshToken and issues new accessToken
     * 3. Server responds with Set-Cookie headers containing new tokens
     * 4. Browser stores new tokens in HttpOnly cookies
     * 5. Frontend receives response body (no tokens, just metadata)
     */
    refreshToken: builder.mutation<TokenRefreshResponse, TokenRefreshRequest>({
      query: (body) => ({
        url: '/api/oauth/token',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const { useRefreshTokenMutation } = tokenApi;
